import {
  Dispatch, FC, forwardRef, SyntheticEvent, useCallback, useState
} from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Slide, Stack, TableCell, TableRow, styled, tableCellClasses, Accordion,
  AccordionSummary, List, ListItem, ListItemText, Typography, AccordionDetails,
  Box, Divider, Card, CardContent, CardActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dateFormat from "dateformat";
import { AxiosInstance } from "axios";
import { TransitionProps } from "@mui/material/transitions";
import { LoadingButton } from "@mui/lab";
import { DataCout } from "./Cout";
import { DataRef } from "../../../../types/DataRef";
import { AlertType } from "../../../../types/AlertType";
import { btnStyle } from "../user/Tabs/utilisateur/UtilisateurRow";
import UpdateCoutForm from "../../../forms/Cout/UpdateCoutForm";
import OptionalWidthModal from "../../../../commons/optional-width-modal/OptionalWidthModal";
import CheckAttr from "../../../../utils/CheckAttr";
import { ConnectedUser } from "../../../../utils/utils";
import { profilToAccessToAllFonctionnality, PROFIL_GESTIONNER_HEURE_PROD } from "../../../../constants/Constants";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    textAlign: "center",
    color: "#fff",
  },
}));

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CoutRowProps {
  cout: DataCout;
  dataPays: DataRef[];
  http: AxiosInstance;
  isDesktopOrLaptop: boolean;
  idCoutsToHaveHours: number[];
  expanded: number | false;
  setExpanded: Dispatch<React.SetStateAction<number | false>>;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
  reloadList: () => void;
}

const CoutRow: FC<CoutRowProps> = ({
  cout,
  dataPays,
  http,
  isDesktopOrLaptop,
  idCoutsToHaveHours,
  expanded,
  setExpanded,
  reloadList,
  setOpenSnack,
  setAlertType,
  setAlerteMessage,
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalDup, setOpenModalDup] = useState<boolean>(false);
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const user = ConnectedUser();

  const handleDelete = useCallback(() => {
    setDeleteLoading(true);
    http
      .delete(`cout/delete/${cout?.id}`)
      .then(() => {
        setOpenModalDelete(false);
        setDeleteLoading(false);
        setAlerteMessage("Suppresion efféctuée !");
        setAlertType("success");
        setOpenSnack(true);
        reloadList();
      })
      .catch((error) => {
        console.log(error);
        setAlertType("error");
        setOpenSnack(true);
      });
  }, []);

  const handleCloseDeleteModal = () => {
    setOpenModalDelete(false);
  };

  let titleModal = "";
  if (openModal) {
    titleModal = "Modifier un cout";
  }
  if (openModalDup) {
    titleModal = "Dupliquer un cout";
  }

  const handleClose = () => {
    titleModal = "";
    setOpenModal(false);
    setOpenModalDup(false);
  };

  const disableEditBtn = (): boolean => {
    if (
      cout?.date_fin != null ||
      (cout?.hasSaisie &&
        !(profilToAccessToAllFonctionnality.includes(user?.profils?.libelle)
          || user?.profils?.libelle === PROFIL_GESTIONNER_HEURE_PROD)) ||
      cout?.article_actif === 0
    ) {
      return true;
    }

    return false;
  };

  const disableDuplicateBtn = (): boolean => {
    if (CheckAttr(cout?.date_fin) || cout?.article_actif === 0 || [PROFIL_GESTIONNER_HEURE_PROD].includes(user?.profils?.libelle)) {
      return true;
    }

    return false;
  };

  const handleChangeAccordionValue = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      {isDesktopOrLaptop ? (
        <TableRow
          sx={{ display: { xs: "none", sm: "none", md: "table-row" } }}
        >
          <StyledTableCell>{cout?.pays_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.bu_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.client_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.operation_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.socifact_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.code_gesco_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.code_article_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.article_libelle}</StyledTableCell>
          <StyledTableCell>{cout?.cout}</StyledTableCell>
          <StyledTableCell>
            {dateFormat(cout?.date_debut, "dd-mm-yyyy")}
          </StyledTableCell>
          <StyledTableCell>
            {cout?.date_fin && dateFormat(cout?.date_fin, "dd-mm-yyyy")}
          </StyledTableCell>
          <StyledTableCell>{cout?.devise_libelle}</StyledTableCell>
          <StyledTableCell>
            <Stack
              direction="row"
              spacing={1}
              display="flex"
              justifyContent="center"
            >
              <Button
                size="small"
                variant="outlined"
                sx={btnStyle({
                  bgColor: "#070d23",
                  hoverColor: "Gray",
                  color: "#fff",
                })}
                startIcon={<BorderColorIcon />}
                disabled={disableEditBtn()}
                onClick={() => setOpenModal(true)}
              >
                Modifier
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<ContentCopyIcon />}
                sx={{ textTransform: "none" }}
                disabled={disableDuplicateBtn()}
                onClick={() => setOpenModalDup(true)}
              >
                Dupliquer
              </Button>
            </Stack>
          </StyledTableCell>
        </TableRow>
      ) : (
        <Accordion
          expanded={expanded === cout?.id}
          onChange={handleChangeAccordionValue(cout?.id as number)}
          sx={{
            display: { xs: "inherit", sm: "inherit", md: "none" },
            borderRadius: 1,
            bgcolor: "#fff",
            mb: 1,
            ml: 0.1
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#080d24" }} />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Stack direction="column">
              <List sx={{ py: 0 }} dense>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Désignation Article:"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {cout?.article_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Code Article :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {cout?.code_article_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Date début :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {dateFormat(cout?.date_debut, "dd-mm-yyyy")}
                      </Typography>
                    }
                  />
                </ListItem>
                {cout?.date_fin && (
                  <ListItem sx={{ my: 0, py: 0 }} dense>
                    <ListItemText
                      primary="Date fin :"
                      secondary={
                        <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                          {dateFormat(cout?.date_fin, "dd-mm-yyyy")}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Coût :"
                    secondary={
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", color: "#FC8404" }}
                      >
                        {cout?.cout}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              <Button
                variant="contained"
                sx={btnStyle({
                  bgColor: "Gray",
                  hoverColor: "Gray",
                  color: "#fff",
                })}
                size="small"
                fullWidth
                startIcon={<BorderColorIcon />}
                disabled={disableEditBtn()}
                onClick={() => setOpenModal(true)}
              >
                Modifier
              </Button>

              <Box>
                <Typography gutterBottom variant="h5" component="div" sx={{ mb: 0, mt: 2 }} >
                  Détails
                </Typography>
                <Divider orientation="horizontal" sx={{ bgcolor: "#fff" }} />
              </Box>

              <Card>
                <CardContent>
                  <List dense>
                    <ListItem dense>
                      <ListItemText
                        primary="Code Gesco :"
                        secondary={cout?.code_gesco_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Société facturation"
                        secondary={cout?.socifact_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Opération"
                        secondary={cout?.operation_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Client"
                        secondary={cout?.client_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText primary="Bu" secondary={cout?.bu_libelle} />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Pays"
                        secondary={cout?.pays_libelle}
                      />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    color="warning"
                    sx={{ textTransform: 'none' }}
                    disabled={disableDuplicateBtn()}
                    onClick={() => setOpenModalDup(true)}
                  >
                    Dupliquer
                  </Button>
                </CardActions>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <OptionalWidthModal
        title={titleModal}
        breackPoint="md"
        onClose={handleClose}
        open={CheckAttr(titleModal)}
      >
        <UpdateCoutForm
          cout={cout}
          dataPays={dataPays}
          http={http}
          openModal={openModal}
          openModalDup={openModalDup}
          onClose={handleClose}
          setOpenSnack={setOpenSnack}
          setAlerteMessage={setAlerteMessage}
          setAlertType={setAlertType}
          reloadList={reloadList}
        />
      </OptionalWidthModal>

      <Dialog
        open={openModalDelete}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDeleteModal}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Vous êtes sur le point de supprimer ce cout, êtes-vous sûr de
            vouloir poursuivre cette action ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={() => handleDelete()}
            startIcon={<DeleteIcon />}
            loading={deleteLoading}
            loadingPosition="end"
            sx={btnStyle({
              bgColor: "#FF0000",
              hoverColor: "#FE3737",
            })}
            variant="contained"
          >
            Oui
          </LoadingButton>
          <LoadingButton
            onClick={() => handleCloseDeleteModal()}
            startIcon={<ExitToAppIcon />}
            sx={{ textTransform: "none" }}
            loadingPosition="end"
            variant="contained"
            color="inherit"
          >
            Non
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoutRow;
